import React, { useMemo, useState } from 'react';
import { Columns, Check, ChevronDown, ChevronRight } from 'lucide-react';
import WidgetCard from './WidgetCard';
import { useDashboardStore } from '../../../store/useDashboardStore';
import { ValuationData } from '../../../types/valuation';

// ─── Formatters ───────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000).toLocaleString()}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function formatNumber(value: number): string {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000).toLocaleString()}K`;
  return Math.round(value).toLocaleString();
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatDecimal(value: number): string {
  return value.toFixed(2);
}

// ─── Calculation helpers ──────────────────────────────────────────────────────

function calculateEstimatedValue(data: ValuationData): number {
  if (data.apiResponse?.result?.total_station_value) {
    return data.apiResponse.result.total_station_value;
  }
  const fuelRevenue = (data.annualGallons || 0) * (data.fuelMargin || 0);
  const storeRevenue = (data.storeRevenue || 0) * 12;
  const expenses = (data.operatingExpenses || 0) * 12;
  return (fuelRevenue + storeRevenue - expenses) * 3.5 || 0;
}

function calculateGrossRevenue(data: ValuationData): number {
  if (data.apiResponse?.result?.gross_revenue) {
    return data.apiResponse.result.gross_revenue;
  }
  const fuelRevenue = (data.annualGallons || 0) * (data.fuelMargin || 0);
  const storeRevenue = (data.storeRevenue || 0) * 12;
  return fuelRevenue + storeRevenue;
}

function calculateGrossProfit(data: ValuationData): number {
  if (data.apiResponse?.result?.gross_profit) {
    return data.apiResponse.result.gross_profit;
  }
  const fuelProfit = (data.annualGallons || 0) * (data.fuelMargin || 0);
  const storeProfit = (data.storeRevenue || 0) * 12 * (data.storeMargin || 0.3);
  return fuelProfit + storeProfit;
}

function calculateNOI(data: ValuationData): number {
  if (data.apiResponse?.result?.tam_estimation?.estimates?.year_1_noi) {
    return data.apiResponse.result.tam_estimation.estimates.year_1_noi;
  }
  const fuelProfit = (data.annualGallons || 0) * (data.fuelMargin || 0);
  const storeProfit = (data.storeRevenue || 0) * 12 * (data.storeMargin || 0.3);
  const expenses = (data.operatingExpenses || 0) * 12;
  return fuelProfit + storeProfit - expenses;
}

function calculateCapRate(data: ValuationData): number {
  const noi = calculateNOI(data);
  const value = calculateEstimatedValue(data);
  if (value === 0) return 0;
  return noi / value;
}

function calculateTotalDepreciation(data: ValuationData): number {
  const assets = data.apiResponse?.result?.depreciated_assets;
  if (!assets) return 0;
  let total = 0;
  for (const key of Object.keys(assets)) {
    const asset = assets[key];
    if (asset?.Annual_Depreciation) total += asset.Annual_Depreciation;
  }
  return total;
}

function calculateTaxShield(data: ValuationData): number {
  return calculateTotalDepreciation(data) * 0.35;
}

function calculateMaxLoanDSCR(data: ValuationData): number {
  const noi = calculateNOI(data);
  if (noi <= 0) return 0;
  const monthlyRate = 0.075 / 12;
  const n = 20 * 12;
  const mortgageConstant =
    ((monthlyRate * Math.pow(1 + monthlyRate, n)) /
      (Math.pow(1 + monthlyRate, n) - 1)) *
    12;
  return noi / 1.35 / mortgageConstant;
}

function calculateLTV(data: ValuationData): number {
  const maxLoan = calculateMaxLoanDSCR(data);
  const value = calculateEstimatedValue(data);
  if (value === 0) return 0;
  return maxLoan / value;
}

function getSwotCounts(data: ValuationData): {
  strengths: number;
  weaknesses: number;
} {
  let strengths = 0;
  let weaknesses = 0;
  if ((data.annualGallons || 0) > 1620000) strengths++;
  else if (
    (data.annualGallons || 0) > 0 &&
    (data.annualGallons || 0) < 1200000
  )
    weaknesses++;
  if ((data.fuelMargin || 0) > 0.28) strengths++;
  else if ((data.fuelMargin || 0) > 0 && (data.fuelMargin || 0) < 0.2)
    weaknesses++;
  if ((data.storeRevenue || 0) > 68333) strengths++;
  else if ((data.storeRevenue || 0) > 0 && (data.storeRevenue || 0) < 40000)
    weaknesses++;
  if ((data.operatingExpenses || 0) > 0 && (data.operatingExpenses || 0) < 40000)
    strengths++;
  else if ((data.operatingExpenses || 0) > 55000) weaknesses++;
  if ((data.trafficCount || 0) > 25000) strengths++;
  else if ((data.trafficCount || 0) > 0 && (data.trafficCount || 0) < 15000)
    weaknesses++;
  if (data.carWashRevenue && data.carWashRevenue > 0) strengths++;
  if (data.foodServiceRevenue && data.foodServiceRevenue > 0) strengths++;
  if (data.propertyCondition === 'Poor' || data.propertyCondition === 'Fair')
    weaknesses++;
  if (
    data.propertyCondition === 'Excellent' ||
    data.propertyCondition === 'Good'
  )
    strengths++;
  return { strengths: Math.max(strengths, 1), weaknesses };
}

function getTotalImprovements(data: ValuationData): number {
  const assets = data.apiResponse?.result?.depreciated_assets;
  if (!assets) return 0;
  let total = 0;
  for (const key of Object.keys(assets)) {
    if (assets[key]?.Depreciated_Cost) total += assets[key]!.Depreciated_Cost;
  }
  return total;
}

function safeDiv(numerator: number, denominator: number): number {
  if (denominator === 0) return 0;
  return numerator / denominator;
}

// ─── Metric definitions ──────────────────────────────────────────────────────

interface MetricRow {
  label: string;
  getValue: (data: ValuationData) => number | string;
  format: (value: number | string) => string;
  higherIsBetter: boolean | null;
}

interface MetricSection {
  key: string;
  title: string;
  defaultOpen: boolean;
  rows: MetricRow[];
}

const metricSections: MetricSection[] = [
  {
    key: 'valuation',
    title: 'Valuation',
    defaultOpen: true,
    rows: [
      {
        label: 'Total Estimated Value',
        getValue: calculateEstimatedValue,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Land Value',
        getValue: (d) => d.apiResponse?.result?.predicted_land_value || 0,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Improvements (Depreciated)',
        getValue: getTotalImprovements,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Business Value',
        getValue: (d) => d.apiResponse?.result?.predicted_business_value || 0,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
    ],
  },
  {
    key: 'operations',
    title: 'Operations',
    defaultOpen: true,
    rows: [
      {
        label: 'Annual Fuel Volume',
        getValue: (d) => d.annualGallons || 0,
        format: (v) => `${formatNumber(v as number)} gal`,
        higherIsBetter: true,
      },
      {
        label: 'Fuel Margin',
        getValue: (d) => d.fuelMargin || 0,
        format: (v) => `$${(v as number).toFixed(2)}/gal`,
        higherIsBetter: true,
      },
      {
        label: 'C-Store Revenue (mo)',
        getValue: (d) => d.storeRevenue || 0,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Operating Expenses (mo)',
        getValue: (d) => d.operatingExpenses || 0,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: false,
      },
      {
        label: 'Property Size',
        getValue: (d) => d.propertySize || 0,
        format: (v) => `${formatNumber(v as number)} SF`,
        higherIsBetter: null,
      },
      {
        label: 'Lot Size',
        getValue: (d) => d.lotSize || 0,
        format: (v) => `${formatNumber(v as number)} SF`,
        higherIsBetter: null,
      },
      {
        label: 'Daily Traffic',
        getValue: (d) => d.trafficCount || 0,
        format: (v) => `${formatNumber(v as number)} VPD`,
        higherIsBetter: true,
      },
      {
        label: 'Fuel Pumps',
        getValue: (d) => d.numberOfPumps || 0,
        format: (v) => `${v}`,
        higherIsBetter: null,
      },
    ],
  },
  {
    key: 'efficiency',
    title: 'Efficiency (per SF)',
    defaultOpen: false,
    rows: [
      {
        label: 'Value / SF',
        getValue: (d) =>
          safeDiv(calculateEstimatedValue(d), d.propertySize || 0),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'NOI / SF',
        getValue: (d) => safeDiv(calculateNOI(d), d.propertySize || 0),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Gross Revenue / SF',
        getValue: (d) =>
          safeDiv(calculateGrossRevenue(d), d.propertySize || 0),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'C-Store Revenue / SF (ann.)',
        getValue: (d) =>
          safeDiv((d.storeRevenue || 0) * 12, d.propertySize || 0),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Fuel Gallons / SF',
        getValue: (d) =>
          safeDiv(d.annualGallons || 0, d.propertySize || 0),
        format: (v) => formatDecimal(v as number),
        higherIsBetter: true,
      },
      {
        label: 'OpEx / SF (ann.)',
        getValue: (d) =>
          safeDiv((d.operatingExpenses || 0) * 12, d.propertySize || 0),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: false,
      },
    ],
  },
  {
    key: 'revenue-efficiency',
    title: 'Revenue Efficiency',
    defaultOpen: false,
    rows: [
      {
        label: 'Gallons / Pump',
        getValue: (d) =>
          safeDiv(d.annualGallons || 0, d.numberOfPumps || 0),
        format: (v) => `${formatNumber(v as number)} gal`,
        higherIsBetter: true,
      },
      {
        label: 'Fuel Profit / Traffic Count',
        getValue: (d) =>
          safeDiv(
            (d.annualGallons || 0) * (d.fuelMargin || 0),
            (d.trafficCount || 0) * 365
          ),
        format: (v) => `$${(v as number).toFixed(3)}`,
        higherIsBetter: true,
      },
      {
        label: 'Revenue per Traffic Count',
        getValue: (d) =>
          safeDiv(
            calculateGrossRevenue(d),
            (d.trafficCount || 0) * 365
          ),
        format: (v) => `$${(v as number).toFixed(2)}`,
        higherIsBetter: true,
      },
      {
        label: 'Gross Profit Multiple',
        getValue: (d) =>
          safeDiv(calculateEstimatedValue(d), calculateGrossProfit(d)),
        format: (v) => `${(v as number).toFixed(2)}x`,
        higherIsBetter: false,
      },
      {
        label: 'OpEx Ratio',
        getValue: (d) =>
          safeDiv(
            (d.operatingExpenses || 0) * 12,
            calculateGrossRevenue(d)
          ),
        format: (v) => formatPercent(v as number),
        higherIsBetter: false,
      },
      {
        label: 'C-Store Margin',
        getValue: (d) => d.storeMargin || 0,
        format: (v) => formatPercent(v as number),
        higherIsBetter: true,
      },
    ],
  },
  {
    key: 'financial',
    title: 'Financial Analysis',
    defaultOpen: true,
    rows: [
      {
        label: 'Net Operating Income',
        getValue: calculateNOI,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Cap Rate',
        getValue: calculateCapRate,
        format: (v) => formatPercent(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Gross Profit',
        getValue: calculateGrossProfit,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Annual Depreciation',
        getValue: calculateTotalDepreciation,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Tax Shield (est. 35%)',
        getValue: calculateTaxShield,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'After-Tax NOI',
        getValue: (d) => calculateNOI(d) + calculateTaxShield(d),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
    ],
  },
  {
    key: 'loan',
    title: 'Loan Analysis',
    defaultOpen: false,
    rows: [
      {
        label: 'Max Loan (1.35x DSCR)',
        getValue: calculateMaxLoanDSCR,
        format: (v) => formatCurrency(v as number),
        higherIsBetter: true,
      },
      {
        label: 'Loan-to-Value',
        getValue: calculateLTV,
        format: (v) => formatPercent(v as number),
        higherIsBetter: null,
      },
      {
        label: 'Required Equity',
        getValue: (d) =>
          Math.max(
            calculateEstimatedValue(d) - calculateMaxLoanDSCR(d),
            0
          ),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: false,
      },
      {
        label: 'Cash-on-Cash Return',
        getValue: (d) => {
          const equity = Math.max(
            calculateEstimatedValue(d) - calculateMaxLoanDSCR(d),
            0
          );
          if (equity === 0) return 0;
          const noi = calculateNOI(d);
          const monthlyRate = 0.075 / 12;
          const n = 20 * 12;
          const loan = calculateMaxLoanDSCR(d);
          const annualDebt =
            loan *
            ((monthlyRate * Math.pow(1 + monthlyRate, n)) /
              (Math.pow(1 + monthlyRate, n) - 1)) *
            12;
          return (noi - annualDebt) / equity;
        },
        format: (v) => formatPercent(v as number),
        higherIsBetter: true,
      },
    ],
  },
  {
    key: 'portfolio',
    title: 'Portfolio Metrics',
    defaultOpen: false,
    rows: [
      {
        label: 'Price / Gallon',
        getValue: (d) =>
          safeDiv(calculateEstimatedValue(d), d.annualGallons || 0),
        format: (v) => `$${(v as number).toFixed(2)}/gal`,
        higherIsBetter: false,
      },
      {
        label: 'Price / Lot SF',
        getValue: (d) =>
          safeDiv(calculateEstimatedValue(d), d.lotSize || 0),
        format: (v) => formatCurrency(v as number),
        higherIsBetter: null,
      },
      {
        label: 'Revenue Streams',
        getValue: (d) => {
          let count = 0;
          if ((d.annualGallons || 0) > 0) count++;
          if ((d.storeRevenue || 0) > 0) count++;
          if (d.carWashRevenue && d.carWashRevenue > 0) count++;
          if (d.foodServiceRevenue && d.foodServiceRevenue > 0) count++;
          if (d.hasLeasedQSR) count++;
          if (d.qsrRepairBayRevenue && d.qsrRepairBayRevenue > 0) count++;
          return count;
        },
        format: (v) => `${v} active`,
        higherIsBetter: true,
      },
      {
        label: 'Brand',
        getValue: (d) =>
          d.brandAffiliation === 'branded'
            ? d.brandName || 'Branded'
            : 'Unbranded',
        format: (v) => v as string,
        higherIsBetter: null,
      },
      {
        label: 'Ownership',
        getValue: (d) =>
          d.ownership === 'owned' ? 'Fee Simple' : `Leased (${d.leaseTermYears || '?'}yr)`,
        format: (v) => v as string,
        higherIsBetter: null,
      },
    ],
  },
  {
    key: 'swot',
    title: 'SWOT Summary',
    defaultOpen: false,
    rows: [
      {
        label: 'Key Strengths',
        getValue: (d) => getSwotCounts(d).strengths,
        format: (v) => `${v} identified`,
        higherIsBetter: true,
      },
      {
        label: 'Key Weaknesses',
        getValue: (d) => getSwotCounts(d).weaknesses,
        format: (v) => `${v} identified`,
        higherIsBetter: false,
      },
      {
        label: 'Condition',
        getValue: (d) => d.propertyCondition || 'N/A',
        format: (v) => v as string,
        higherIsBetter: null,
      },
      {
        label: 'Year Built',
        getValue: (d) => d.yearBuilt || 'N/A',
        format: (v) => `${v}`,
        higherIsBetter: null,
      },
      {
        label: 'Car Wash',
        getValue: (d) =>
          d.hasCarWash
            ? d.carWashType
              ? d.carWashType.charAt(0).toUpperCase() + d.carWashType.slice(1)
              : 'Yes'
            : 'None',
        format: (v) => v as string,
        higherIsBetter: null,
      },
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function ComparisonMatrixWidget() {
  const {
    valuations,
    selectedPropertyIds,
    togglePropertySelection,
    isLoadingValuations,
    valuationsError,
  } = useDashboardStore();

  const selectedValuations = useMemo(
    () => valuations.filter((v) => selectedPropertyIds.includes(v.id)),
    [valuations, selectedPropertyIds]
  );

  // Track which sections are open — default from section config
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      metricSections.forEach((s) => {
        initial[s.key] = s.defaultOpen;
      });
      return initial;
    }
  );

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    metricSections.forEach((s) => {
      next[s.key] = true;
    });
    setOpenSections(next);
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    metricSections.forEach((s) => {
      next[s.key] = false;
    });
    setOpenSections(next);
  };

  const allOpen = metricSections.every((s) => openSections[s.key]);

  return (
    <WidgetCard
      title="Comparison Matrix"
      icon={<Columns className="w-5 h-5" />}
      colSpan={3}
      isLoading={isLoadingValuations}
      error={valuationsError}
    >
      {valuations.length < 2 ? (
        <p className="text-gray-500 text-sm">
          Run at least 2 valuations to compare properties side by side.
        </p>
      ) : (
        <div className="space-y-4">
          {/* Property selector */}
          <div>
            <p className="text-xs text-gray-500 mb-2">
              Select up to 4 properties to compare (
              {selectedPropertyIds.length} selected)
            </p>
            <div className="flex flex-wrap gap-2">
              {valuations.map((v) => {
                const isSelected = selectedPropertyIds.includes(v.id);
                return (
                  <button
                    key={v.id}
                    onClick={() => togglePropertySelection(v.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${
                      isSelected
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                    {(v.data.location || 'Unnamed').substring(0, 30)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Comparison table */}
          {selectedValuations.length >= 2 && (
            <div>
              {/* Expand / Collapse controls */}
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-400">
                  Click a category to expand or collapse
                </p>
                <button
                  onClick={allOpen ? collapseAll : expandAll}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  {allOpen ? 'Collapse All' : 'Expand All'}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-2 pr-4 text-gray-500 font-medium w-[200px]">
                        Metric
                      </th>
                      {selectedValuations.map((v) => (
                        <th
                          key={v.id}
                          className="text-right py-2 px-3 text-gray-700 font-medium max-w-[160px] truncate"
                        >
                          {(v.data.location || 'Unnamed').substring(0, 25)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {metricSections.map((section) => {
                      const isOpen = openSections[section.key];
                      return (
                        <React.Fragment key={section.key}>
                          {/* Collapsible section header */}
                          <tr
                            onClick={() => toggleSection(section.key)}
                            className="cursor-pointer select-none group"
                          >
                            <td
                              colSpan={selectedValuations.length + 1}
                              className="pt-3 pb-1"
                            >
                              <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                                {isOpen ? (
                                  <ChevronDown className="w-3.5 h-3.5" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5" />
                                )}
                                {section.title}
                                {!isOpen && (
                                  <span className="text-[10px] font-normal normal-case tracking-normal text-gray-400 ml-1">
                                    ({section.rows.length} metrics)
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Section rows (shown only when open) */}
                          {isOpen &&
                            section.rows.map((metric) => {
                              const values = selectedValuations.map((v) =>
                                metric.getValue(v.data)
                              );
                              const numericValues = values.filter(
                                (v): v is number =>
                                  typeof v === 'number' && v !== 0
                              );
                              const best =
                                metric.higherIsBetter === null ||
                                numericValues.length === 0
                                  ? null
                                  : metric.higherIsBetter
                                  ? Math.max(...numericValues)
                                  : Math.min(...numericValues);

                              return (
                                <tr
                                  key={metric.label}
                                  className="border-b border-gray-100"
                                >
                                  <td className="py-1.5 pr-4 text-gray-600 pl-5">
                                    {metric.label}
                                  </td>
                                  {selectedValuations.map((v, i) => {
                                    const val = values[i];
                                    const isBest =
                                      best !== null &&
                                      typeof val === 'number' &&
                                      val === best &&
                                      val !== 0;
                                    const isEmpty =
                                      val === 0 ||
                                      val === 'N/A' ||
                                      val === '';
                                    return (
                                      <td
                                        key={v.id}
                                        className={`text-right py-1.5 px-3 font-medium ${
                                          isEmpty
                                            ? 'text-gray-300'
                                            : isBest
                                            ? 'text-green-700 bg-green-50'
                                            : 'text-gray-700'
                                        }`}
                                      >
                                        {isEmpty ? '—' : metric.format(val)}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedValuations.length < 2 && (
            <p className="text-gray-400 text-xs italic">
              Select at least 2 properties above to see the comparison.
            </p>
          )}
        </div>
      )}
    </WidgetCard>
  );
}
